import React, { FC, ReactNode } from 'react';
import { Entry } from 'contentful';
import { HeaderSize, HeaderText } from '@/components/ui/HeaderText';
import { Badge, LuvImage, UniversalLink } from '@/components';
import {
  TypeImage,
  TypeMarketingCardFields,
  TypeLink_externalFields,
  TypeLink_internalFields,
} from '@/core/generated-types';
import { shouldHideWithLocaleOverride } from '@/utils/localeOverride';
import { ChevronIcon } from '@/components/ui/Icons';

type MarketingCardWrapperProps = {
  link: Entry<TypeLink_externalFields | TypeLink_internalFields> | undefined;
  children: ReactNode;
};

const MarketingCardWrapper: FC<MarketingCardWrapperProps> = ({
  link,
  children,
}): React.JSX.Element | null => {
  return link ? (
    <UniversalLink link={link} hideArrow={true} classes="group">
      {children}
    </UniversalLink>
  ) : (
    <>{children}</>
  );
};

const MarketingCard: FC<TypeMarketingCardFields> = ({
  entryName,
  title,
  description,
  marketingCardImage,
  marketingCardLink,
  isSquare,
  hasShadow,
  localeOverride,
  badge,
}) => {
  const rounded = isSquare ? 'rounded' : '';
  const roundedTop = isSquare ? 'rounded-t' : '';
  const shadow = hasShadow
    ? 'drop-shadow-md group-hover:drop-shadow-lg group-hover:-translate-y-px group-hover:transition'
    : '';

  if (shouldHideWithLocaleOverride(localeOverride)) {
    return null;
  }

  return (
    <MarketingCardWrapper link={marketingCardLink}>
      <div
        className={`max-h-137 h-full w-fit max-w-[390px] bg-white ${rounded} ${shadow} mx-auto flex flex-col`}
        data-testid={`marketing-card-${entryName}`}
      >
        <div className="relative">
          {badge && (
            <div className="absolute left-6 top-6 z-[1] md:left-7 md:top-8">
              <Badge {...badge.fields} />
            </div>
          )}
          <figure className={`${roundedTop} h-97 relative z-[0]`}>
            {marketingCardImage && (
              <LuvImage
                height={385}
                width={391}
                alt={marketingCardImage.fields?.altText}
                imageData={marketingCardImage as TypeImage}
                className={`custom h-[300px] w-full lg:h-[385px] ${
                  isSquare && 'aspect-square'
                } object-cover ${roundedTop}`}
              />
            )}
          </figure>
        </div>
        <div className="flex grow flex-col justify-between p-6 text-left">
          <HeaderText
            size={HeaderSize.H3}
            text={title}
            classes="capitalize mb-0.5"
          />
          {description && (
            <p className="leading-5 md:leading-6">{description}</p>
          )}
          {marketingCardLink && (
            <div className="mt-3 flex grow flex-col justify-end">
              {/*
                <span> is used instead of UniversalCta so there's not an <a> nested inside
                another <a>
              */}
              <span className="flex flex-row items-center gap-1.5 font-bold text-indigo group-hover:text-indigo-dark">
                {marketingCardLink.fields?.linkText}
                <ChevronIcon
                  color="indigo"
                  direction="right"
                  height={13}
                  width={13}
                  strokeWidth={3}
                  classes="relative top-[1px]"
                />
              </span>
            </div>
          )}
        </div>
      </div>
    </MarketingCardWrapper>
  );
};

export default React.memo(MarketingCard);
